<script lang="ts" setup>
import { inject, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { ViewerEvents } from '@immersive-tech/viewer-engine';
import ToggleBox from '@/components/ToggleBox.vue';
import CollapseFieldset from '@/components/CollapseFieldset.vue';

import type { Ref } from 'vue';
import type { Viewer, Animations, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IAnimation } from '@/types/IAnimation';
import type { IManifestHotspot } from '@/types/IManifest';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const animations = ref<{ [key: string]: IAnimation }>({});
const hotspot = inject('hotspot') as Ref<IManifestHotspot>;
const animationsExtension = viewer.value?.extensions.get('Animations') as Extension<Animations>;

const buildAnimationObjectTree = () => {
  const animationNames = Array.from(animationsExtension.animationActionFunctions.keys());

  for (const animationName of animationNames) {
    const manifestAnimation = hotspot.value.animations.find((animation) => animation[0] === animationName);

    if (!(animationName in animations.value)) {
      animations.value[animationName] = {
        enabled: manifestAnimation ? true : false,
        direction: (() => {
          if (!manifestAnimation) return 'forward';
          if (manifestAnimation[1] >= 0) return 'forward';

          return 'backward';
        })(),
        speed: manifestAnimation ? Math.abs(manifestAnimation[1]) : 1,
      };
    }
  }
};

const addEventListeners = () => {
  viewer.value?.addEventListener(ViewerEvents.MODEL_ADDED_TO_SCENE, buildAnimationObjectTree);
  viewer.value?.addEventListener(ViewerEvents.MODEL_DESTROYED, buildAnimationObjectTree);
};

const removeEventListeners = () => {
  viewer.value?.removeEventListener(ViewerEvents.MODEL_ADDED_TO_SCENE, buildAnimationObjectTree);
  viewer.value?.removeEventListener(ViewerEvents.MODEL_DESTROYED, buildAnimationObjectTree);
};

const mountHandler = () => {
  if (!viewer.value) return;

  const animationKeys = Object.keys(animations.value);

  for (const key of animationKeys) {
    delete animations.value[key];
  }

  removeEventListeners();
  buildAnimationObjectTree();
  addEventListeners();
};

const beforeUnmountHandler = () => {
  removeEventListeners();
};

const animationWatcher = (newValue: typeof animations.value) => {
  const newValueEntries = Object.entries(newValue);

  for (const [animationName, animation] of newValueEntries) {
    const manifestAnimationIndex = hotspot.value.animations.findIndex((animation) => animation[0] === animationName);

    if (animation.enabled) {
      const animationSpeed = animation.direction === 'forward' ? animation.speed : -animation.speed;

      if (manifestAnimationIndex === -1) {
        hotspot.value.animations.push([animationName, animationSpeed]);
      } else {
        hotspot.value.animations[manifestAnimationIndex][1] = animationSpeed;
      }
    } else {
      if (manifestAnimationIndex !== -1) {
        hotspot.value.animations.splice(manifestAnimationIndex, 1);
      }
    }
  }
};

onMounted(mountHandler);
onBeforeUnmount(beforeUnmountHandler);
watch(animations, animationWatcher, { deep: true });
</script>

<template>
  <CollapseFieldset>
    <template #legend>
      Animations
    </template>

    <template v-if="Object.keys(animations).length === 0">
      <span class="text-xs">No animations found</span>
    </template>

    <CollapseFieldset v-for="animationName in Object.keys(animations)" :key="animationName">
      <template #legend>
        {{ animationName }}
      </template>

      <div :class="{ 'mb-1': animations[animationName].enabled === true }">
        <ToggleBox v-model="animations[animationName].enabled" label="Enabled" />
      </div>

      <template v-if="animations[animationName].enabled === true">
        <div class="flex flex-row mb-1 items-center">
          <label class="text-xs mr-2">Direction</label>
          <select v-model="animations[animationName].direction" class="text-xs border py-0.5 flex-grow">
            <option value="forward">
              Forward
            </option>
            <option value="backward">
              Backward
            </option>
          </select>
        </div>

        <div class="flex flex-row items-center">
          <label class="text-xs mr-2">Speed</label>
          <input
            v-model="animations[animationName].speed"
            type="number"
            min="0.01"
            :max="Number.MAX_SAFE_INTEGER"
            step="0.01"
            class="text-xs px-1 border py-0.5 flex-grow"
          >
        </div>
      </template>
    </CollapseFieldset>
  </CollapseFieldset>
</template>
